import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { TokenSource } from 'livekit-client';
import { SessionProvider, useSession } from '@livekit/components-react';

interface GuestConnectionContextType {
  isConnectionActive: boolean;
  connect: () => void;
  startDisconnectTransition: () => void;
  startReconnectTransition: () => boolean;
  onDisconnectTransitionComplete: () => void;
  connectionError: string | null;
  participantName: string;
}

const GuestConnectionContext = createContext<GuestConnectionContextType>({
  isConnectionActive: false,
  connect: () => {},
  startDisconnectTransition: () => {},
  startReconnectTransition: () => false,
  onDisconnectTransitionComplete: () => {},
  connectionError: null,
  participantName: '',
});

export function useGuestConnection() {
  const ctx = useContext(GuestConnectionContext);
  if (!ctx) {
    throw new Error('useGuestConnection must be used within a GuestConnectionProvider');
  }
  return ctx;
}

interface GuestConnectionProviderProps {
  participantId: string;
  designId: string;
  children: React.ReactNode;
}

const MAX_AUTO_RETRIES = 1;

export function GuestConnectionProvider({
  participantId,
  designId,
  children,
}: GuestConnectionProviderProps) {
  const [isConnectionActive, setIsConnectionActive] = useState(false);
  const [pendingReconnect, setPendingReconnect] = useState(false);
  const [, setRetryCount] = useState(0);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [participantName, setParticipantName] = useState('');

  const tokenSource = useMemo(() => {
    return TokenSource.custom(async () => {
      const url = new URL('/api/guest-connection', window.location.origin);
      url.searchParams.set('participantId', participantId);
      url.searchParams.set('designId', designId);

      console.log('[GuestConnection] Fetching token:', url.toString());

      try {
        const res = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
          const errorMessage = errorData.error || `HTTP ${res.status}`;
          console.error('[GuestConnection] Error:', errorMessage);
          setConnectionError(errorMessage);
          throw new Error(errorMessage);
        }

        const data = await res.json();
        console.log('[GuestConnection] Got token for:', data.participantName);
        setParticipantName(data.participantName || 'Guest');
        setConnectionError(null);
        return data;
      } catch (error) {
        console.error('[GuestConnection] Error fetching connection details:', error);
        const message = error instanceof Error ? error.message : 'Connection failed';
        setConnectionError(message);
        throw error;
      }
    });
  }, [participantId, designId]);

  const session = useSession(tokenSource, undefined);

  const { start: startSession, end: endSession } = session;

  const startConnection = useCallback(
    (resetRetryCount: boolean) => {
      if (resetRetryCount) {
        setRetryCount(0);
      }
      setPendingReconnect(false);
      setIsConnectionActive(true);
      startSession();
    },
    [startSession]
  );

  const connect = useCallback(() => {
    startConnection(true);
  }, [startConnection]);

  const startDisconnectTransition = useCallback(() => {
    setPendingReconnect(false);
    setIsConnectionActive(false);
  }, []);

  const startReconnectTransition = useCallback(() => {
    let scheduled = false;
    setIsConnectionActive(false);
    setRetryCount((count) => {
      if (count >= MAX_AUTO_RETRIES) {
        setPendingReconnect(false);
        scheduled = false;
        return count;
      }
      setPendingReconnect(true);
      scheduled = true;
      return count + 1;
    });
    return scheduled;
  }, []);

  const onDisconnectTransitionComplete = useCallback(() => {
    endSession();
    if (pendingReconnect) {
      startConnection(false);
    }
  }, [endSession, pendingReconnect, startConnection]);

  const value = useMemo(() => {
    return {
      isConnectionActive,
      connect,
      startDisconnectTransition,
      startReconnectTransition,
      onDisconnectTransitionComplete,
      connectionError,
      participantName,
    };
  }, [
    connect,
    isConnectionActive,
    onDisconnectTransitionComplete,
    startDisconnectTransition,
    startReconnectTransition,
    connectionError,
    participantName,
  ]);

  return (
    <SessionProvider session={session}>
      <GuestConnectionContext.Provider value={value}>{children}</GuestConnectionContext.Provider>
    </SessionProvider>
  );
}
